import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, Loading, EmptyState, StatusBadge, StatCard } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LifeBuoy, Plus, Loader2, AlertTriangle, Clock, Inbox } from "lucide-react";
import { DiscussButton } from "@/components/DiscussButton";

const PRIORITIES = ["low", "medium", "high", "critical"];
const CATEGORIES = ["Technical", "Billing", "Service", "General"];
const STATUSES = ["open", "in_progress", "waiting", "resolved"];

export default function Support() {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [open, setOpen] = useState(params.get("new") === "1");
  const [f, setF] = useState({ subject: "", customer_name: "", priority: "medium", category: "Technical" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const { data: tickets = [], isLoading } = useQuery({ queryKey: ["tickets"], queryFn: async () => (await api.get("/tickets")).data });

  const createMut = useMutation({
    mutationFn: (body) => api.post("/tickets", { ...body, status: "open", sla_status: "healthy", ticket_number: `TKT-${Math.floor(3000 + Math.random() * 7000)}` }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tickets"] }); setOpen(false); toast.success("Ticket created"); },
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }) => api.patch(`/tickets/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tickets"] }); toast.success("Ticket updated"); },
  });

  const openCount = tickets.filter((t) => t.status !== "resolved").length;
  const breached = tickets.filter((t) => t.sla_status === "breached").length;
  const critical = tickets.filter((t) => t.priority === "critical").length;

  return (
    <div>
      <PageHeader title="Support" subtitle="Tickets, SLA & escalation management" testid="support-header">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="new-ticket-btn"><Plus className="mr-1.5 h-4 w-4" /> New Ticket</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>Create ticket</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Subject</Label><Input value={f.subject} onChange={(e) => set("subject", e.target.value)} data-testid="ticket-subject-input" /></div>
              <div className="space-y-1.5"><Label>Customer</Label><Input value={f.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="ticket-customer-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Priority</Label>
                  <Select value={f.priority} onValueChange={(v) => set("priority", v)}><SelectTrigger data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-1.5"><Label>Category</Label>
                  <Select value={f.category} onValueChange={(v) => set("category", v)}><SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                </div>
              </div>
              <DialogFooter><Button onClick={() => createMut.mutate(f)} disabled={createMut.isPending || !f.subject} data-testid="ticket-save-btn">{createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button></DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard icon={Inbox} label="Open tickets" value={openCount} tone="info" index={0} testid="tkt-open" />
        <StatCard icon={Clock} label="SLA breached" value={breached} tone="critical" index={1} testid="tkt-breached" />
        <StatCard icon={AlertTriangle} label="Critical" value={critical} tone="warning" index={2} testid="tkt-critical" />
      </div>

      {isLoading ? <Loading /> : tickets.length === 0 ? (
        <EmptyState icon={LifeBuoy} title="No tickets yet" description="Customer issues and complaints will appear here with SLA tracking."
          action={<Button onClick={() => setOpen(true)} className="rounded-full"><Plus className="mr-1.5 h-4 w-4" /> New Ticket</Button>} testid="tickets-empty" />
      ) : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Ticket</TableHead><TableHead>Customer</TableHead><TableHead>Priority</TableHead>
              <TableHead>SLA</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {tickets.map((t) => (
                <TableRow key={t.id} data-testid={`ticket-row-${t.id}`}>
                  <TableCell>
                    <p className="font-medium">{t.subject}</p>
                    <p className="text-xs text-muted-foreground">{t.ticket_number} · {t.category}</p>
                  </TableCell>
                  <TableCell>{t.customer_name}</TableCell>
                  <TableCell><StatusBadge value={t.priority} /></TableCell>
                  <TableCell><StatusBadge value={t.sla_status} /></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Select value={t.status} onValueChange={(v) => updateMut.mutate({ id: t.id, body: { status: v } })}>
                        <SelectTrigger className="h-8 w-32" data-testid={`ticket-status-${t.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                      </Select>
                      <DiscussButton contextType="ticket" contextId={t.id} label={`${t.ticket_number} · ${t.subject}`} iconOnly variant="ghost" className="h-8 w-8" />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
